import React, { useContext, useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  OutlinedInput,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { useMutation } from '@apollo/client';
import { UserContext } from '../../auth/auth-context';
import { useSelectedBusiness } from '../../../redux/store';
import { ADD_BOOKING_EXCEPTION, REMOVE_BOOKINGS, SEND_EMAIL_MUTATION } from '../../../graphql/mutations';
import { expandBookings } from '../../../util/booking/expandBookings';
import { useCalendar } from '../calendar/calendarContext';

const CancelSchedule = ({
  currentData,   
  showCancelModal,  // Modal visibility state passed from the parent
  setShowCancelModal,  // Function to close the modal 
  answersData,
}) => {
  const { bookingDatas, setBookingDatas, refetchBookings } = useCalendar();
  const [message, setMessage] = useState("");
  const [cancelOption, setCancelOption] = useState("single"); // default option for recurring bookings
  const userContext = useContext(UserContext);
  const providerUser = userContext.providerUser; 
  const selectedBusiness = useSelectedBusiness(); 



  const [sendEmailMutation] = useMutation(SEND_EMAIL_MUTATION);
  const [removeBookingMutation] = useMutation(REMOVE_BOOKINGS, {
    update(cache, { data: { removeBooking } }) {
      // Evict the deleted booking from the cache and run garbage collection.
      const normalizedId = cache.identify(removeBooking);
      cache.evict({ id: normalizedId });
      cache.gc();
    },
  });

  const [addBookingExceptionMutation] = useMutation(ADD_BOOKING_EXCEPTION);


  const handleRefetch = async () => {
    try {
      // Call refetch to get the new data
      await refetchBookings({ fetchPolicy: 'network-only' }); 
    } catch (error) {
      console.error("Error during refetching bookings:", error);
    }
  };

  const answers = answersData?.getBookingAnswers;

  const findEmailByBookingId = (bookingId) => {
    const safeBookingData = [].concat(bookingDatas || []).flat();
    const targetId = Number(bookingId);
    if (isNaN(targetId)) return null;
    const booking = safeBookingData.find(b =>
      b?.id !== undefined && Number(b.id) === targetId
    );
    if (!booking) return null;
    const answerQuestionId = answers?.find(answer =>
      Number(answer?.bookingId) === Number(booking?.id)
    )?.id;
    if (!answerQuestionId) return null;
    const bAnswers = (Array.isArray(answers) ? answers : [])
      .filter(answer =>
        Number(answer?.bookingId) === targetId
    );
    const groupedAnswers = bAnswers.reduce((acc, cur) => {
      const aId = cur?.id;
      if (aId !== undefined) {
        acc[aId] = acc[aId] || [];
        acc[aId].push(cur?.answer || '');
      }
      return acc;
    }, {});
    return groupedAnswers[answerQuestionId]?.join(", ") || null;
  };

  // Determine if the booking is recurring based on the first item in currentData.
  const isRecurring =
    currentData &&
    currentData.length > 0 &&
    currentData[0].isRecurring  

  // Define a cancellation message.
  const cancellationMessage = "Please confirm that you would like to cancel this booking. A cancellation email will be sent to all involved parties.";

  // Handle the cancellation logic and email sending.
  const handleBookingCancellation = (bookings, message) => {
    const normalizedBookings = Array.isArray(bookings)
      ? bookings.filter(Boolean)
      : bookings ? [bookings] : [];
    if (normalizedBookings.length === 0) {
      console.error("No valid bookings to cancel");
      return;
    }

    if (isRecurring) {
      if (cancelOption === "series") {
    // Cancel entire series: mark each booking as cancelled.
        normalizedBookings.forEach((booking) => {
          if (!booking?.id) {
            console.error("Invalid booking object:", booking);
            return;
          }
          removeBookingMutation({
            variables: {
              bookingId: parseInt(booking.id, 10),
            },
          })
            .then(() => {
              handleRefetch();
              let toEmail = "";
              const attendyEmail = findEmailByBookingId(booking.id);
              toEmail = attendyEmail === providerUser.email ? booking.staffEmail : attendyEmail;
              const fromEmail = attendyEmail === providerUser.email ? attendyEmail : booking.staffEmail;
              if (toEmail) {
                const meetingTime = new Date(booking.time).toLocaleString();
                const emailBody =
                `<p>Hello,</p>
                  <p>
                    Your <strong>${booking.bookingTypeName}</strong> meeting 
                    with <strong>${providerUser?.username || "the provider"}</strong>, scheduled for 
                    <strong>${meetingTime}</strong>, has been cancelled.
                  </p>
                  ${message ? `<p>Note: "${message}"</p>` : ""}
                  <p>
                    If you have any questions or would like to reschedule, please contact us.
                  </p>
                  <p>Best regards,</p>
                `;
                sendEmailMutation({
                  variables: {
                    from_email: fromEmail,
                    to: toEmail,
                    subject: "Meeting Cancellation Notification",
                    text:
                      `Hello,
                    Your ${booking.bookingTypeName} meeting with ${providerUser?.username || "the provider"}, scheduled for ${meetingTime}, has been cancelled.
                    ${message ? `Note: "${message}"` : ""}
                    If you have any questions or would like to reschedule, please contact the provider.
                    Best regards,`,
                    html: emailBody,
                  },
                });
              }
            })
            .catch((error) => {
              console.error(`Error processing booking ${booking.id}:`, error);
            });
        });
      } else {
        // Cancel single instance: add exception for this occurrence.
        normalizedBookings.forEach((booking) => {
          const occurrenceDate = booking.time; // the occurrence to cancel.
          addBookingExceptionMutation({
            variables: {
              input: {
                bookingId: parseInt(booking.id, 10),
                exceptionDate: occurrenceDate,
                reason: message,
              },
            },
          })
            .then(() => {
              handleRefetch();
            })
            .catch((error) => {
              console.error(`Error processing exception for booking ${booking.id}:`, error);
            });
        });
      }
    } else {
      // Non-recurring booking cancellation.
      normalizedBookings.forEach((booking) => {
        if (!booking?.id) {
          console.error("Invalid booking object:", booking);
          return;
        }
        removeBookingMutation({
          variables: {
            bookingId: parseInt(booking.id, 10),
          },
        })
          .then(() => {
            handleRefetch();
            let toEmail = "";
            const attendyEmail = findEmailByBookingId(booking.id);
            toEmail = attendyEmail === providerUser.email ? booking.staffEmail : attendyEmail;
            const fromEmail = attendyEmail === providerUser.email ? attendyEmail : booking.staffEmail;
            if (toEmail) {
              const meetingTime = new Date(booking.time).toLocaleString();
              const emailBody = `
                <p>Hello,</p>
                <p>
                  Your <strong>${booking.bookingTypeName}</strong> meeting 
                  with <strong>${providerUser?.username || "the provider"}</strong>, scheduled for 
                  <strong>${meetingTime}</strong>, has been cancelled.
                </p>
                ${message ? `<p>Note: "${message}"</p>` : ""}
                <p>
                  If you have any questions or would like to reschedule, please contact us.
                </p>
                <p>Best regards,</p>
              `;
              sendEmailMutation({
                variables: {
                  from_email: fromEmail,
                  to: toEmail,
                  subject: "Meeting Cancellation Notification",
                  text: `Hello,
Your ${booking.bookingTypeName} meeting with ${providerUser?.username || "the provider"}, scheduled for ${meetingTime}, has been cancelled.
${message ? `Note: "${message}"` : ""}
If you have any questions or would like to reschedule, please contact the provider.
Best regards,`,
                  html: emailBody,
                },
              });
            }
          })
          .catch((error) => {
            console.error(`Error processing booking ${booking.id}:`, error);
          });
      });
    }
    setShowCancelModal(false);
  };

  return (
    <Modal open={showCancelModal} onClose={() => setShowCancelModal(false)}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: '10px',
          outline: 'none',
          maxWidth: '300px',
        }}
      >
        <Typography variant="h6" mb={2}>
          Cancel Booking
        </Typography>
        <Typography variant="body1" mb={2}>
          {isRecurring
            ? "This is a recurring booking. Would you like to cancel the entire series or just this instance?"
            : "Are you sure you want to cancel this booking?"}
        </Typography>
        {isRecurring && (
          <RadioGroup
            row
            value={cancelOption}
            onChange={(e) => setCancelOption(e.target.value)}
          >
            <FormControlLabel
              value="single"
              control={<Radio />}
              label="Cancel This Instance"
            />
            <FormControlLabel
              value="series"
              control={<Radio />}
              label="Cancel Entire Series"
            />
          </RadioGroup>
        )}
        {/* Display the cancellation message */}
        <Typography variant="caption" color="text.secondary" mb={2}>
          {cancellationMessage}
        </Typography>
        <OutlinedInput
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          fullWidth
          sx={{ borderRadius: '10px', my: 2 }}
          placeholder="Add an optional message"
          multiline
          minRows={4}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '20px' }}>
          <Button variant="outlined" onClick={() => setShowCancelModal(false)}>
            Keep Booking
          </Button>
          <Button variant="outlined" onClick={() => handleBookingCancellation(currentData, message)}>
            Confirm Cancellation
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CancelSchedule;
